import { formatChargedQty, type ChargedUnitLabel } from '@estimator/shared';
import { useState } from 'react';
import WoodFinishBreakdown from '../shared/WoodFinishBreakdown';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { FabricPackingResult, WoodFinishRow } from '@estimator/shared';

export interface SeatCushionMaterialDetail {
  material_id: string;
  material_name: string;
  total_sqft: number;
  sqft_with_waste: number;
  cost: number;
  /** What was billed and in which unit, for the breakdown label. */
  charged_qty?: number;
  charged_unit?: ChargedUnitLabel;
  num_sheets?: number;
  sheet_size?: string;
  currency_note?: string;
}

export interface SeatCushionSlotDetail {
  slot_id?: string;
  name: string;
  calc_mode: string;
  real_inches: number;
  sqft: number;
  cost: number;
  material_name?: string;
  num_sheets?: number;
  currency_note?: string;
  material_id?: string;
  material_category?: string;
  material_subtype?: string | null;
  fabric_pieces?: Array<{
    panel_type: 'front' | 'side' | 'back';
    section_index: number;
    panel_index?: number;
    width_in: number;
    length_in: number;
    design_up_axis: 'width' | 'length';
  }>;
  waste_factor_used?: number;
  sheet_size?: string;
  note?: string;
}

export interface SeatCushionHardwareItem {
  name: string;
  qty: number;
  cost_per_unit: number;
  cost: number;
  unit?: string;
  currency_note?: string;
}

export interface SeatCushionCostPreviewResult {
  framing_cost: number;
  fabric_cost: number;
  finish_cost: number;
  foam_cost: number;
  dacron_cost: number;
  hardware_cost: number;
  total_cost: number;
  wood_finish_cost: number;
  wood_finish_details: WoodFinishRow[];
  total_cost_excluding_fabric: number;
  rate_per_lf: number;
  framing_materials: SeatCushionMaterialDetail[];
  foam_materials: SeatCushionMaterialDetail[];
  slot_details: SeatCushionSlotDetail[];
  rib_count: number;
  spring_count: number;
  channel_count_per_section: number;
  button_count: number;
  hardware_items: SeatCushionHardwareItem[];
  hardware_warnings?: string[];
  fabric_packings: FabricPackingResult[];
}

interface SeatCushionCostPreviewProps {
  cost: SeatCushionCostPreviewResult | null;
  lfQuantity: number;
  sectionCount: number;
  laborCost?: number;
  cncCost?: number;
  laborDetails?: Array<{
    rate_name: string;
    category: string;
    unit: string;
    units: number;
    cost: number;
    score_breakdown?: Array<{ label: string; minutes: number; cost: number; basis?: 'per_section' | 'total' }>;
  }>;
  /** Library only: multiplies Total ex-Fabric and Rate so the editor shows the
   *  number the Library prices. Fabric is never multiplied. */
  oversizeFactor?: number;
}

function fmtMinutes(n: number): string {
  return Math.abs(n - Math.round(n)) < 0.005 ? String(Math.round(n)) : n.toFixed(2);
}

function rowMinutes(
  row: { minutes: number; basis?: 'per_section' | 'total' },
  sectionCount: number,
): { perSec: number; total: number } {
  const safeSc = sectionCount > 0 ? sectionCount : 1;
  if (row.basis === 'total') {
    return { perSec: row.minutes / safeSc, total: row.minutes };
  }
  return { perSec: row.minutes, total: row.minutes * safeSc };
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

const VALUE_COL_MIN_WIDTH = '64px';

function CardHeader({ title, showPerSection }: { title: string; showPerSection: boolean }) {
  return (
    <div className="flex justify-between items-baseline mb-1.5">
      <div className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {showPerSection && (
        <div className="flex gap-3 text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          <span className="text-right" style={{ minWidth: VALUE_COL_MIN_WIDTH }}>Per Sec</span>
          <span className="text-right" style={{ minWidth: VALUE_COL_MIN_WIDTH }}>Total</span>
        </div>
      )}
    </div>
  );
}

function ValueCells({
  total,
  sectionCount,
  showPerSection,
  size = 'xs',
}: {
  total: number;
  sectionCount: number;
  showPerSection: boolean;
  size?: 'xs' | '10';
}) {
  const sizeClass = size === 'xs' ? 'text-xs' : 'text-[10px]';
  const perSection = sectionCount > 0 ? total / sectionCount : 0;
  return (
    <span className={`font-medium ml-3 flex-shrink-0 flex gap-3 ${sizeClass}`}>
      {showPerSection && (
        <span className="tabular-nums text-right" style={{ minWidth: VALUE_COL_MIN_WIDTH, color: 'var(--text-secondary)' }}>
          {fmt(perSection)}
        </span>
      )}
      <span className="tabular-nums text-right" style={{ minWidth: VALUE_COL_MIN_WIDTH, color: 'var(--text-primary)' }}>
        {fmt(total)}
      </span>
    </span>
  );
}

export default function SeatCushionCostPreview({ cost, lfQuantity, sectionCount, laborCost, laborDetails, cncCost, oversizeFactor = 1 }: SeatCushionCostPreviewProps) {
  const [expanded, setExpanded] = useState(false);

  if (!cost) return null;

  const showPerSection = sectionCount > 1;
  const exFabric = (cost.total_cost_excluding_fabric + (laborCost ?? 0) + (cncCost ?? 0)) * oversizeFactor;

  const hasDetails =
    cost.framing_materials.length > 0 ||
    cost.slot_details.some((s) => s.cost > 0) ||
    cost.foam_cost > 0 ||
    cost.dacron_cost > 0 ||
    cost.hardware_items.length > 0 ||
    cost.wood_finish_details.length > 0;

  return (
    <div className="rounded-lg" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}>
      <div
        className="flex items-center gap-6 px-4 py-2.5 cursor-pointer select-none"
        onClick={() => hasDetails && setExpanded(!expanded)}
        style={{ borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#e67e22' }} />
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#e67e22' }}>Cost</span>
        </div>

        <div className="flex items-center gap-6 flex-1 flex-wrap">
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Framing</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.framing_cost)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Fabric</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.fabric_cost)}</span>
          </div>
          {cost.finish_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Finish</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.finish_cost)}</span>
            </div>
          )}
            {cost.wood_finish_cost > 0 && (
              <div>
                <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Finishing</span>
                <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.wood_finish_cost)}</span>
              </div>
            )}
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Foam</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.foam_cost)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Dacron</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.dacron_cost)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Hardware</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.hardware_cost)}</span>
          </div>
          {laborCost != null && laborCost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Labor</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(laborCost)}</span>
            </div>
          )}
          {cncCost != null && cncCost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>CNC</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cncCost)}</span>
            </div>
          )}
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Total ex-Fabric</span>
            <span className="text-sm font-bold ml-1.5" style={{ color: '#e67e22' }}>{fmt(exFabric)}</span>
          </div>
          {lfQuantity > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Rate</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-secondary)' }}>{fmt(exFabric / lfQuantity)}/LF</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          {cost.spring_count > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Springs</span>
              <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>{cost.spring_count}</span>
            </div>
          )}
          {cost.channel_count_per_section > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Channels</span>
              <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>{cost.channel_count_per_section}</span>
            </div>
          )}
          {cost.button_count > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Buttons</span>
              <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>{cost.button_count}</span>
            </div>
          )}
          {cost.rib_count > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Ribs</span>
              <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>{cost.rib_count}</span>
            </div>
          )}
        </div>

        {hasDetails && (
          expanded
            ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} />
            : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
        )}
      </div>

      {(cost.hardware_warnings ?? []).length > 0 && (
        <div
          className="px-4 py-1.5 text-[11px]"
          style={{ color: '#e67e22', borderTop: '1px solid var(--border)' }}
        >
          {(cost.hardware_warnings ?? []).map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      )}

      {expanded && <WoodFinishBreakdown rows={cost.wood_finish_details} />}
      {expanded && (
        <div className="px-4 py-3 grid grid-cols-2 gap-4">
          {cost.framing_materials.length > 0 && (
            <div>
              <CardHeader title="Framing Materials" showPerSection={showPerSection} />
              {cost.framing_materials.map((m, i) => (
                <div key={i} className="mb-0.5">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {m.material_name} <span style={{ color: 'var(--text-muted)' }}>({m.charged_qty != null && m.charged_unit ? formatChargedQty(m.charged_qty, m.charged_unit) : `${m.total_sqft.toFixed(1)} sqft`}{m.num_sheets && m.charged_unit !== 'sht' ? `, ${m.num_sheets} sht` : ''})</span>
                    </span>
                    <ValueCells total={m.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                  </div>
                  {m.currency_note && (
                    <div className="text-[10px] ml-2" style={{ color: '#e67e22' }}>{m.currency_note}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(cost.foam_cost > 0 || cost.dacron_cost > 0) && (
            <div>
              <CardHeader title="Foam & Dacron" showPerSection={showPerSection} />
              {cost.foam_materials?.length > 0 ? (
                cost.foam_materials.map((m, i) => (
                  <div key={i} className="mb-0.5">
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {m.material_name} <span style={{ color: 'var(--text-muted)' }}>({m.total_sqft.toFixed(1)} sqft{m.num_sheets ? `, ${m.num_sheets} sht` : ''})</span>
                      </span>
                      <ValueCells total={m.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                    </div>
                    {m.currency_note && (
                      <div className="text-[10px] ml-2" style={{ color: '#e67e22' }}>{m.currency_note}</div>
                    )}
                  </div>
                ))
              ) : cost.foam_cost > 0 ? (
                <div className="flex justify-between text-xs mb-0.5">
                  <span style={{ color: 'var(--text-secondary)' }}>Foam</span>
                  <ValueCells total={cost.foam_cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                </div>
              ) : null}
              {cost.dacron_cost > 0 && (
                <div className="flex justify-between text-xs mb-0.5">
                  <span style={{ color: 'var(--text-secondary)' }}>Dacron</span>
                  <ValueCells total={cost.dacron_cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                </div>
              )}
            </div>
          )}

          {cost.hardware_items.length > 0 && (
            <div>
              <CardHeader title="Hardware" showPerSection={showPerSection} />
              {cost.hardware_items.map((h, i) => (
                <div key={i} className="mb-0.5">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {h.name} <span style={{ color: 'var(--text-muted)' }}>x{h.qty} @ {fmt(h.cost_per_unit)}</span>
                    </span>
                    <ValueCells total={h.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                  </div>
                  {h.currency_note && (
                    <div className="text-[10px] ml-2" style={{ color: '#e67e22' }}>{h.currency_note}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {laborDetails && laborDetails.length > 0 && (
            <div>
              <CardHeader title="Labor" showPerSection={showPerSection} />
              {laborDetails.map((d, i) => (
                <div key={i} className="mb-1">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {d.rate_name} <span style={{ color: 'var(--text-muted)' }}>({d.units} {d.unit})</span>
                    </span>
                    <ValueCells total={d.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                  </div>
                  {d.score_breakdown && d.score_breakdown.length > 0 && (
                    <div className="ml-3 mt-0.5 pl-2 border-l border-[var(--border)]">
                      {d.score_breakdown.map((row, j) => {
                        const m = rowMinutes(row, sectionCount);
                        return (
                          <div key={j} className="flex justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            <span>
                              {row.label}{' '}
                              <span className="tabular-nums">
                                ({showPerSection ? `${fmtMinutes(m.perSec)} / ${fmtMinutes(m.total)}` : fmtMinutes(m.total)} min)
                              </span>
                            </span>
                            <ValueCells total={row.cost} sectionCount={sectionCount} showPerSection={showPerSection} size="10" />
                          </div>
                        );
                      })}
                      {(() => {
                        const totals = d.score_breakdown.reduce(
                          (acc, r) => {
                            const m = rowMinutes(r, sectionCount);
                            return { perSec: acc.perSec + m.perSec, total: acc.total + m.total, cost: acc.cost + r.cost };
                          },
                          { perSec: 0, total: 0, cost: 0 },
                        );
                        return (
                          <div
                            className="flex justify-between text-[10px] font-semibold mt-0.5 pt-0.5 border-t border-[var(--border)]"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <span>
                              Complexity Score{' '}
                              <span className="tabular-nums">
                                ({showPerSection
                                  ? `${fmtMinutes(totals.perSec)} / ${fmtMinutes(totals.total)}`
                                  : fmtMinutes(totals.total)} min)
                              </span>
                            </span>
                            <ValueCells
                              total={totals.cost}
                              sectionCount={sectionCount}
                              showPerSection={showPerSection}
                              size="10"
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {cost.slot_details.some((s) => s.cost > 0 && s.fabric_pieces && s.fabric_pieces.length > 0) && (
            <div>
              <CardHeader title="Fabric" showPerSection={showPerSection} />
              {cost.slot_details.filter((s) => s.cost > 0 && s.fabric_pieces && s.fabric_pieces.length > 0).map((s, i) => (
                <div key={i} className="mb-1">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {s.name} <span style={{ color: 'var(--text-muted)' }}>({s.sqft.toFixed(1)} sqft)</span>
                    </span>
                    <ValueCells total={s.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                  </div>
                  {s.fabric_pieces && s.fabric_pieces.length > 0 && (
                    <div className="text-[10px] ml-2" style={{ color: 'var(--text-muted)' }}>
                      {s.fabric_pieces.length} cut piece{s.fabric_pieces.length !== 1 ? 's' : ''} — see Finish Schedule for roll layout
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {cost.slot_details.some((s) => s.cost > 0 && (!s.fabric_pieces || s.fabric_pieces.length === 0)) && (
            <div>
              <CardHeader title="Finish" showPerSection={showPerSection} />
              {cost.slot_details.filter((s) => s.cost > 0 && (!s.fabric_pieces || s.fabric_pieces.length === 0)).map((s, i) => (
                <div key={i} className="mb-1">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {s.name} <span style={{ color: 'var(--text-muted)' }}>({s.sqft.toFixed(1)} sqft)</span>
                    </span>
                    <ValueCells total={s.cost} sectionCount={sectionCount} showPerSection={showPerSection} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
